import React, { useCallback, useState } from 'react';
import { Box, Divider, Stack, Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

import { FirstLoginTooltip, MyRoomLink, NotificationSettings } from 'components';
import { IFirstLoginTooltipsContext } from 'context';
import { useFirstLoginTooltips } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ApiUser } from 'types';
import { getFullName } from 'utils';
import { CircleCrossIcon, HeavyCheckIcon } from 'assets';

import styles from './MyRoom.module.scss';

export function MyRoom() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl =
    i18n.resolvedLanguage?.toLowerCase().startsWith('ar') ||
    i18n.dir(i18n.resolvedLanguage) === 'rtl' ||
    (typeof document !== 'undefined' && document.documentElement.dir === 'rtl');

  const user: ApiUser = useAppSelector(selectUser)!;
  const { tooltipsState, accept }: IFirstLoginTooltipsContext = useFirstLoginTooltips();

  const [open, setOpen] = useState(false);
  const [copyButtonAnchorEl, setCopyButtonAnchorEl] = useState<HTMLButtonElement | null>(null);

  const onCopyToClipboard = useCallback(() => {
    setOpen(true);
    setTimeout(() => setOpen(false), 1500);
  }, []);

  return (
    <>
      <Box className={styles.content}>
        <Box className={styles.section}>
          {user.clinicUser.roomName && (
            <Stack direction='column' className={styles.welcomeStatusBlock}>
              <Stack
                direction='column'
                spacing={0.35}
                alignItems={isRtl ? 'flex-end' : 'flex-start'}
                sx={{ width: '100%' }}
                className={styles.greeting}
              >
                <Typography component='p' variant='h2' sx={{ width: '100%', textAlign: isRtl ? 'right' : 'left' }}>
                  {t('welcomeUser', { name: getFullName(user) })}
                </Typography>
              </Stack>

              {!isMobile && (
                <Stack direction='row' alignItems='center' className={styles.statusRow}>
                  <Stack direction='row' alignItems='center' className={styles.statusContent}>
                    {user.clinicUser.unavailable ? (
                      <CircleCrossIcon sx={{ color: '#626363', fontSize: '1.6rem' }} />
                    ) : (
                      <HeavyCheckIcon />
                    )}
                    <Typography component='p' variant='body2' className={styles.statusText}>
                      {user.clinicUser.unavailable ? t('unavailable') : t('available')}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

          {!isMobile && (
            <Typography component='p' variant='body2' sx={{ textAlign: isRtl ? 'right' : 'left' }}>
              {t('shareLinkInvite')}
            </Typography>
          )}

          <Tooltip open={open} title={t('copiedToClipboard')} placement='right'>
            <div style={{ width: '100%' }}>
              <FirstLoginTooltip
                text={t('copyThisLinkTooltip')}
                numberText={t('firstTooltipStep')}
                open={tooltipsState === 1}
                onClick={accept}
                placement='bottom'
                anchorEl={copyButtonAnchorEl}
                className={styles.firstTooltip}
                arrowClassName={styles.tooltipArrow}
              >
                <Box sx={{ width: '100%' }}>
                  <CopyToClipboard
                    text={`${window.location.origin}/${user.clinicUser.roomName}`}
                    onCopy={onCopyToClipboard}
                  >
                    <MyRoomLink
                      type='copy'
                      value={user.clinicUser.roomName}
                      onClick={onCopyToClipboard}
                      copyButtonRef={(el) => setCopyButtonAnchorEl(el)}
                    />
                  </CopyToClipboard>
                </Box>
              </FirstLoginTooltip>
            </div>
          </Tooltip>
        </Box>

        {!isMobile && (
          <>
            <Divider />
            <NotificationSettings />
          </>
        )}
      </Box>
    </>
  );
}
